/**
 * Phase 2 Wave 1 — Mesh pool / geometry recycle for `LineSegmentsGeometry`.
 *
 * # Problem
 *
 * `sceneBulkLoadHelpers.ts::buildBucketMeshes` allocates a fresh
 * `LineSegmentsGeometry` per bucket on every bulkLoad pass. A typical
 * drawing has dozens to hundreds of buckets (one per layer × color ×
 * linetype × split). On large fixtures the allocator + GC pressure
 * dominates the rebuild — most bucket sizes recur across bulkLoads
 * (same drawing, same layers, same materials).
 *
 * # Design
 *
 * Free-list keyed by capacity-bucket. We round the requested vertex
 * capacity up to the next power of two (256, 512, 1024 ... 524288).
 * `acquireLineSegmentsGeometry(minCapacity)` pops from the bucket list
 * if available, otherwise allocates a fresh
 * `new LineSegmentsGeometry()`. `releaseLineSegmentsGeometry(g)` reads
 * the bucket the geometry was issued from and pushes it back (capped
 * at 8 instances per bucket; over-cap is `.dispose()`'d and dropped to
 * the GC).
 *
 * # Why power-of-two buckets
 *
 * The actual `setPositions()` call always allocates a fresh
 * `Float32Array` and binds it to the geometry. The geometry shell
 * (`InstancedBufferGeometry` + the various per-instance buffer
 * descriptors) is what we want to reuse — that part is independent of
 * the position payload size. Bucketing by capacity keeps the free-list
 * shallow (16 buckets max) without exact-match thrashing.
 *
 * # Why cap = 8 per bucket
 *
 * A pool that never evicts grows unbounded as drawings get larger or
 * users open multiple files in sequence. 8 × 16 buckets = 128
 * geometries cached at peak — bounded GPU/JS heap footprint at ~few
 * hundred KB while still covering 95 %+ of bulkLoad bucket counts.
 *
 * # Why no exact-capacity hint stored on the geometry
 *
 * `LineSegmentsGeometry.setPositions()` calls
 * `setAttribute("instanceStart", new InstancedInterleavedBuffer(...))`
 * with a brand-new buffer every time, so the previous payload's
 * capacity is irrelevant on reuse — only the geometry's
 * group-counts / attributes-cleared state matter. We attach a
 * `__poolBucket` userData marker so `release` can look up the issued
 * bucket without re-deriving capacity.
 *
 * # AutoCAD parity
 *
 * Internal optimization — no AutoCAD-facing behavior. Render output is
 * bit-identical to the pre-pool path (same geometry class, same
 * positions, same instanceCount).
 */

import { LineSegmentsGeometry } from "../rendering/thickLines";

/** Power-of-two bucket sizes. 256 .. 524 288 vertices. */
const BUCKETS: readonly number[] = [
  256, 512, 1024, 2048, 4096, 8192, 16384, 32768, 65536, 131072, 262144, 524288,
];

/** Max items per bucket before `release` falls back to dispose. */
const MAX_PER_BUCKET = 8;

/** Round `capacity` up to the nearest bucket size; falls back to the
 *  largest bucket when the request exceeds the table. */
function bucketFor(capacity: number): number {
  for (const b of BUCKETS) if (b >= capacity) return b;
  return BUCKETS[BUCKETS.length - 1]!;
}

/**
 * Per-geometry tag attached via `userData.__poolBucket`. Lets
 * `release` recover the original bucket without re-deriving capacity
 * from the geometry's current attributes (which may already be cleared
 * mid-dispose).
 */
const POOL_BUCKET_KEY = "__poolBucket";

const pool = new Map<number, LineSegmentsGeometry[]>();
const stats = { acquired: 0, released: 0, fresh: 0, evicted: 0 };

/**
 * Acquire a `LineSegmentsGeometry` capable of holding at least
 * `minCapacity` line-segment vertices (line pairs). The caller MUST
 * call `setPositions()` (and any per-instance attributes) before use —
 * pooled geometries retain stale GPU state otherwise.
 */
export function acquireLineSegmentsGeometry(
  minCapacity: number,
): LineSegmentsGeometry {
  const bucket = bucketFor(Math.max(minCapacity, 1));
  stats.acquired += 1;
  const list = pool.get(bucket);
  if (list && list.length > 0) {
    const g = list.pop()!;
    g.userData[POOL_BUCKET_KEY] = bucket;
    // Defense-in-depth for the three.js instanced-draw cap. three latches
    // `geometry._maxInstanceCount` once (while `=== undefined`) from the
    // instanceStart `.count` at first render and clears it ONLY on the
    // geometry's `'dispose'` event — which the pool deliberately skips to
    // reuse the shell. A recycled geometry therefore carries the cap from
    // its previous life and would truncate the next draw to that stale
    // count (three.js #21488 / #26363 / #19706). `setPositions` already
    // clears it, but reset here too so a recycled shell can never re-enter
    // service with a stale cap regardless of caller order.
    delete (g as unknown as { _maxInstanceCount?: number })._maxInstanceCount;
    return g;
  }
  const g = new LineSegmentsGeometry();
  g.userData[POOL_BUCKET_KEY] = bucket;
  stats.fresh += 1;
  return g;
}

/**
 * Return a previously-acquired geometry to the pool. If the bucket is
 * full the geometry is `.dispose()`'d and dropped so the pool size
 * stays bounded.
 *
 * It is safe to release a geometry that was NOT obtained via
 * `acquireLineSegmentsGeometry` — we fall back to `.dispose()` when
 * the bucket marker is missing (defensive against mixed call sites
 * during a partial migration).
 */
export function releaseLineSegmentsGeometry(
  g: LineSegmentsGeometry,
): void {
  stats.released += 1;
  const marker = g.userData[POOL_BUCKET_KEY] as number | undefined;
  if (typeof marker !== "number") {
    g.dispose();
    return;
  }
  // Clear group counts but keep the geometry shell — caller MUST
  // overwrite positions on the next acquire so we don't carry old
  // payload references. `setPositions` does this implicitly; we still
  // null the instanceStart / instanceEnd buffers defensively so the
  // CPU-side typed-array can be GC'd in between bulkLoads.
  const attrs = (g as unknown as {
    attributes: Record<string, { array: ArrayLike<number> | null } | undefined>;
  }).attributes;
  // Defensive — three.js typed attributes as readonly arrays but the
  // runtime field is writable on InstancedInterleavedBuffer.
  if (attrs.instanceStart) {
    try {
      attrs.instanceStart.array = null;
    } catch {
      /* non-writable in some builds — ignore */
    }
  }
  if (attrs.instanceEnd) {
    try {
      attrs.instanceEnd.array = null;
    } catch {
      /* non-writable in some builds — ignore */
    }
  }
  let list = pool.get(marker);
  if (!list) {
    list = [];
    pool.set(marker, list);
  }
  if (list.length >= MAX_PER_BUCKET) {
    g.dispose();
    stats.evicted += 1;
    return;
  }
  list.push(g);
}

/** Test / hot-reload hook — disposes every pooled geometry and resets
 *  the stats counter. */
export function clearPool(): void {
  for (const list of pool.values()) {
    for (const g of list) g.dispose();
  }
  pool.clear();
  stats.acquired = 0;
  stats.released = 0;
  stats.fresh = 0;
  stats.evicted = 0;
}

/** Snapshot of the pool's bookkeeping. */
export interface PoolStats {
  /** Total `acquire` calls (lifetime). */
  acquired: number;
  /** Total `release` calls (lifetime). */
  released: number;
  /** Number of geometries currently held in the pool. */
  currentSize: number;
  /** Per-bucket headcount (excludes empty buckets). */
  byBucket: Record<number, number>;
  /** Lifetime allocations (cache miss = bucket empty on acquire). */
  fresh: number;
  /** Lifetime evictions (release that exceeded the per-bucket cap). */
  evicted: number;
}

export function getPoolStats(): PoolStats {
  const byBucket: Record<number, number> = {};
  let currentSize = 0;
  for (const [bucket, list] of pool) {
    if (list.length > 0) byBucket[bucket] = list.length;
    currentSize += list.length;
  }
  return {
    acquired: stats.acquired,
    released: stats.released,
    currentSize,
    byBucket,
    fresh: stats.fresh,
    evicted: stats.evicted,
  };
}
